import { listingUtils_getResourceIdAndSlug } from 'app/shared/utils/listingUtils';
import routeUtils from 'app/shared/utils/routeUtils';

type MountFetchStrategyInput = {
  pathname: string;
  query: Record<string, string | boolean | undefined>;
  areaResourceId: string;
  searchSlug: string;
  isClientSideLoadedPage: boolean;
  isInitialSsrPage: boolean;
};

export type MountFetchStrategy =
  | { type: 'listingsOnly'; pathname: string; query: Record<string, string | boolean | undefined> }
  | { type: 'fullFetch'; pathname: string; query: Record<string, string | boolean | undefined> }
  | { type: 'none' };

// HP-7456: Module-level dataset key that survives component unmount/remount.
// Tracks the URL identity (resourceId + slug + query) of the last successful
// listings fetch. Compared against the current URL to skip redundant refetches
// on HDP → back → SRP. Immune to Redux area mutations from resolveListingArea.
let _lastFetchedDatasetKey: string | null = null;

function _buildDatasetKey(pathname: string, query: Record<string, string | boolean | undefined>): string {
  const { resourceId, slug } = listingUtils_getResourceIdAndSlug(pathname);
  const queryKeys = Object.keys(query)
    .filter((k) => query[k] !== undefined)
    .sort();
  const queryStr = queryKeys.map((k) => `${k}=${query[k]}`).join('&');
  return `${resourceId}:${slug}${queryStr ? '?' + queryStr : ''}`;
}

export function setLastFetchedDatasetKey(pathname: string, query: Record<string, string | boolean | undefined>): void {
  _lastFetchedDatasetKey = _buildDatasetKey(pathname, query);
}

export function getLastFetchedDatasetKey(): string | null {
  return _lastFetchedDatasetKey;
}

// For testing only
export function _resetLastFetchedDatasetKey(): void {
  _lastFetchedDatasetKey = null;
}

export function getMountFetchStrategy({
  pathname,
  query,
  areaResourceId,
  searchSlug,
  isClientSideLoadedPage,
  isInitialSsrPage,
}: MountFetchStrategyInput): MountFetchStrategy {
  const { resourceId: pathResourceId, slug: pathSlug } = listingUtils_getResourceIdAndSlug(pathname);
  const isNearMeUrl = routeUtils.isNearMeUrl(pathSlug);
  const hasResourceIdChangedFromLastMount = !isNearMeUrl && pathResourceId !== areaResourceId;
  const hasSlugChangedFromLastMount = !isNearMeUrl && pathSlug !== searchSlug;
  const hasUserMovedBackToNearMePage =
    isNearMeUrl && isInitialSsrPage === false && !hasResourceIdChangedFromLastMount && !hasSlugChangedFromLastMount;

  const shouldFetchClientSideListings =
    (isClientSideLoadedPage && !isInitialSsrPage) || hasResourceIdChangedFromLastMount || hasUserMovedBackToNearMePage;

  if (!shouldFetchClientSideListings) {
    return { type: 'none' };
  }

  // HP-7456: Skip refetch when the current URL identity matches the last
  // successful listings fetch. This prevents the skeleton teardown on
  // HDP → back → SRP while still refetching for slug changes, area changes,
  // and query param changes (saved searches).
  const currentDatasetKey = _buildDatasetKey(pathname, query);
  if (_lastFetchedDatasetKey === currentDatasetKey) {
    return { type: 'none' };
  }

  const isReturningWithSameSlugAndArea = !hasResourceIdChangedFromLastMount && !hasSlugChangedFromLastMount;
  const hasNonPaginationQueryParams = Object.keys(query).some((k) => k !== 'page');

  if (isReturningWithSameSlugAndArea && !hasNonPaginationQueryParams) {
    return { type: 'listingsOnly', pathname, query };
  }

  return { type: 'fullFetch', pathname, query };
}
